<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { createPdfFirstPagePreviewFromUrl } from 'dashboard/helper/pdfPreviewHelper';
import FileIcon from 'next/icon/FileIcon.vue';
import BaseBubble from './Base.vue';
import PdfConversationViewer from './PdfConversationViewer.vue';
import { useMessageContext } from '../provider.js';
import {
  useDisplaySenderName,
  useGroupParticipantColor,
  useHideSellerName,
} from '../useDisplaySenderName.js';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const { t } = useI18n();
const { attachments, content, orientation, variant, inReplyTo } =
  useMessageContext();

const isOutgoing = computed(() => orientation.value === ORIENTATION.RIGHT);
const isPrivateDocument = computed(
  () => variant.value === MESSAGE_VARIANTS.PRIVATE && isOutgoing.value
);
const displaySenderName = useDisplaySenderName();
const groupSenderColor = useGroupParticipantColor(displaySenderName);
const hideSellerName = useHideSellerName();
const senderName = computed(() =>
  hideSellerName.value ? '' : displaySenderName.value
);
// Aceleres 2026-09-09: o rotulo fecha com dois pontos, como nas outras bolhas
// (ver `senderNameLabel` no Base.vue). Fica separado de `senderName` porque
// esse continua a ser o nome limpo — e ele que vai no `title` do elemento.
const senderNameLabel = computed(() =>
  senderName.value ? `${senderName.value}:` : senderName.value
);

const attachment = computed(() => attachments.value?.[0] || {});
const url = computed(() => attachment.value.dataUrl || '');
const isPdfViewerOpen = ref(false);
const downloadedDocuments = ref(
  LocalStorage.get(LOCAL_STORAGE_KEYS.DOWNLOADED_DOCUMENTS) || {}
);

const decodeFileName = value => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const fileName = computed(() => {
  const metadataName =
    attachment.value.fileName || attachment.value.file_name || '';
  if (metadataName) return decodeFileName(metadataName);

  const cleanUrl = String(url.value || '').split(/[?#]/, 1)[0];
  const name = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);
  return decodeFileName(name) || t('CONVERSATION.UNKNOWN_FILE_TYPE');
});

const fileExtension = computed(() => {
  const metadataExtension =
    attachment.value.extension || attachment.value.fileExtension || '';
  if (metadataExtension) return String(metadataExtension).replace('.', '');

  const match = fileName.value.match(/\.([a-z0-9]+)$/i);
  return match?.[1] || '';
});

const fileTypeLabel = computed(
  () => fileExtension.value.toUpperCase() || t('CONVERSATION.UNKNOWN_FILE_TYPE')
);
const documentPreviewTitle = computed(() =>
  fileName.value.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ')
);

const isPdf = computed(() => {
  const contentType =
    attachment.value.contentType || attachment.value.content_type || '';
  return (
    contentType.toLowerCase() === 'application/pdf' ||
    fileExtension.value.toLowerCase() === 'pdf'
  );
});
const privateDocumentPreviewClass =
  '!h-[94px] !rounded-t-lg !border !border-b-0 !border-n-amber-7/60 !bg-white';

const fileSizeLabel = computed(() => {
  const bytes = Number(
    attachment.value.fileSize ?? attachment.value.file_size ?? 0
  );
  if (!Number.isFinite(bytes) || bytes <= 0) return '';

  const units = ['B', 'KB', 'MB', 'GB'];
  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** unitIndex;
  const roundedValue =
    unitIndex === 0 || value >= 10 ? Math.round(value) : value.toFixed(1);

  return `${roundedValue} ${units[unitIndex]}`;
});

const caption = computed(() => {
  const value = String(content.value || '').trim();
  return ['mídia recebida', 'media received'].includes(value.toLowerCase())
    ? ''
    : value;
});

const openPdfViewer = () => {
  if (!isPdf.value || !url.value) return;
  isPdfViewerOpen.value = true;
};

const closePdfViewer = () => {
  isPdfViewerOpen.value = false;
};

const downloadedDocumentKey = computed(() =>
  String(attachment.value.id || url.value || '')
);

const isDownloaded = computed(
  () => !!downloadedDocuments.value[downloadedDocumentKey.value]
);

const markDocumentAsDownloaded = () => {
  const key = downloadedDocumentKey.value;
  if (!key) return;

  downloadedDocuments.value = {
    ...downloadedDocuments.value,
    [key]: true,
  };
  LocalStorage.set(
    LOCAL_STORAGE_KEYS.DOWNLOADED_DOCUMENTS,
    downloadedDocuments.value
  );
};

const downloadDocument = () => {
  if (!url.value) return;

  markDocumentAsDownloaded();

  const link = document.createElement('a');
  link.href = url.value;
  link.download = fileName.value;
  link.rel = 'noreferrer noopener nofollow';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

const handleFileClick = () => {
  if (isPdf.value) {
    openPdfViewer();
    return;
  }

  if (url.value) downloadDocument();
};

const previewImage = ref('');
const pdfPageCount = ref(0);
const isPreviewLoading = ref(false);

const pdfPageCountLabel = computed(() => {
  if (!isPdf.value || pdfPageCount.value <= 0) return '';
  return pdfPageCount.value === 1
    ? '1 página'
    : `${pdfPageCount.value} páginas`;
});

const generatePreview = async () => {
  if (
    !isPdf.value ||
    !url.value ||
    previewImage.value ||
    isPreviewLoading.value
  ) {
    return;
  }

  isPreviewLoading.value = true;
  try {
    const result = await createPdfFirstPagePreviewFromUrl(url.value);
    previewImage.value = result?.preview || '';
    pdfPageCount.value = Number(result?.pageCount) || 0;
  } catch {
    previewImage.value = '';
    pdfPageCount.value = 0;
  } finally {
    isPreviewLoading.value = false;
  }
};

onMounted(generatePreview);
watch(url, generatePreview);
</script>

<template>
  <div
    class="flex min-w-0 flex-col items-stretch"
    :class="{
      'acl-private-document-wrapper md:w-[348px] md:max-w-full max-md:box-border max-md:w-[calc(var(--acl-mobile-bubble-max-width)+13px)] max-md:max-w-full max-md:pr-[13px]':
        isPrivateDocument,
    }"
  >
    <div
      v-if="senderName"
      class="acl-file-sender-name relative mb-1.5 min-w-0 px-[14px] py-[10px] text-[16.2px] font-semibold leading-tight text-n-slate-12 shadow-sm"
      :class="[
        { hidden: isPrivateDocument },
        isOutgoing
          ? 'rounded-lg rounded-tr-none bg-[#d9fdd3]'
          : 'rounded-lg rounded-tl-none bg-white',
        groupSenderColor,
      ]"
    >
      <div class="truncate" :title="senderName">
        {{ senderNameLabel }}
      </div>
      <span
        aria-hidden="true"
        class="absolute top-0 size-0"
        :class="
          isOutgoing
            ? '-right-[13px] border-r-[14px] border-t-[14px] border-r-transparent border-t-[#d9fdd3]'
            : '-left-[13px] border-l-[14px] border-t-[14px] border-l-transparent border-t-white'
        "
      />
    </div>

    <!-- A citação também exige a altura natural já usada pelo cartão com legenda. -->
    <BaseBubble
      class="acl-document-bubble acl-file-bubble overflow-hidden p-0 [&.acl-file-bubble::after]:!-z-[1]"
      :class="{
        'acl-file-bubble--download-only': !isPdf,
        'acl-file-bubble--with-caption': !!caption || !!inReplyTo,
        'acl-private-document-bubble [--acl-media-surface:#fcefc3] !bg-[var(--acl-media-surface)] !w-full !min-w-0 !max-w-full !h-auto !min-h-0 !max-h-none !rounded-xl !border !border-n-amber-7/60 !p-2': isPrivateDocument,
      }"
      data-bubble-name="attachment"
      :sender-name-above="senderName"
      hide-sender-name
      media-caption
    >
      <template v-if="isPrivateDocument && senderName" #before-private-note>
        <div class="min-w-0 truncate px-1 pb-2 text-base font-semibold text-n-amber-12" :title="senderName">
          {{ senderNameLabel }}
        </div>
      </template>
      <div
        v-if="isPdf && url"
        class="acl-file-bubble__preview cursor-pointer"
        :class="isPrivateDocument ? privateDocumentPreviewClass : ''"
        role="button"
        tabindex="0"
        :aria-label="t('CONVERSATION.ATTACHMENT_VIEW')"
        @click="openPdfViewer"
        @keydown.enter.prevent="openPdfViewer"
        @keydown.space.prevent="openPdfViewer"
      >
        <img v-if="previewImage" :src="previewImage" alt="" />
      </div>
      <div
        v-else-if="isPrivateDocument"
        class="acl-file-bubble__preview flex cursor-pointer flex-col justify-center gap-2 overflow-hidden px-3"
        :class="privateDocumentPreviewClass"
        :role="url ? 'button' : undefined"
        :tabindex="url ? 0 : undefined"
        :aria-label="url ? t('CONVERSATION.DOWNLOAD') : undefined"
        @click="handleFileClick"
        @keydown.enter.prevent="handleFileClick"
        @keydown.space.prevent="handleFileClick"
      >
        <span class="truncate text-xs font-semibold text-n-slate-12">{{ documentPreviewTitle }}</span>
        <span class="h-px w-full bg-n-slate-6" aria-hidden="true" />
        <span class="h-px w-full bg-n-slate-6" aria-hidden="true" />
        <span class="h-px w-full bg-n-slate-6" aria-hidden="true" />
      </div>

      <div
        class="acl-file-bubble__body mx-[5px] mb-[5px] rounded-b-[5px]"
        :class="{
          'cursor-pointer': isPdf || url,
          'acl-file-bubble__body--out': isOutgoing,
          '!mx-0 !mb-0 !rounded-b-lg !border !border-t-0 !border-n-amber-7/60 !bg-[#fcefc3] !p-3': isPrivateDocument,
        }"
        :role="isPdf || url ? 'button' : undefined"
        :tabindex="isPdf || url ? 0 : undefined"
        :aria-label="!isPdf && url ? t('CONVERSATION.DOWNLOAD') : undefined"
        @click="handleFileClick"
        @keydown.enter.prevent="handleFileClick"
        @keydown.space.prevent="handleFileClick"
      >
        <div class="acl-file-bubble__file">
          <div class="acl-file-bubble__icon" aria-hidden="true">
            <FileIcon :file-type="fileExtension" />
          </div>
          <div class="acl-file-bubble__details">
            <div class="acl-file-bubble__name" :class="{ '!text-base !font-semibold !whitespace-normal !break-all': isPrivateDocument }" :title="fileName">
              {{ fileName }}
            </div>
            <div class="acl-file-bubble__info" :class="{ '!text-xs': isPrivateDocument }">
              <span v-if="pdfPageCountLabel">{{ pdfPageCountLabel }}</span>
              <span
                v-if="pdfPageCountLabel"
                class="acl-file-bubble__separator"
                aria-hidden="true"
              />
              <span>{{ fileTypeLabel }}</span>
              <span
                v-if="fileSizeLabel"
                class="acl-file-bubble__separator"
                aria-hidden="true"
              />
              <span v-if="fileSizeLabel">{{ fileSizeLabel }}</span>
            </div>
          </div>

          <span
            v-if="!isPdf && url && !isDownloaded && !isPrivateDocument"
            v-tooltip="$t('CONVERSATION.DOWNLOAD')"
            class="acl-file-bubble__download grid size-12 shrink-0 place-content-center rounded-full bg-black/[0.06] text-n-slate-11 transition-colors hover:bg-black/10"
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" height="34" width="34" fill="currentColor">
              <path
                fill="currentColor"
                d="M12 15.57a1.1 1.1 0 0 1-.38-.06.88.88 0 0 1-.32-.21l-3.6-3.6a.92.92 0 0 1-.29-.7 1.03 1.03 0 0 1 1-1.01c.28-.01.52.08.71.29L11 12.14V5c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v7.15l1.88-1.88c.2-.2.43-.3.7-.28a1.02 1.02 0 0 1 1 1.01c.02.27-.08.5-.28.7l-3.6 3.6a.8.8 0 0 1-.32.21 1.1 1.1 0 0 1-.38.06ZM6 20c-.55 0-1.02-.2-1.41-.59-.4-.39-.59-.86-.59-1.41v-2c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v2h12v-2c0-.28.1-.52.29-.71.19-.2.43-.29.71-.29.28 0 .52.1.71.29.2.19.29.43.29.71v2c0 .55-.2 1.02-.59 1.41-.39.4-.86.59-1.41.59H6Z"
              />
            </svg>
          </span>
        </div>
      </div>

      <!-- Aceleres 2026-09-11: a legenda estava DENTRO de `__body`, o painel
           que serve de fundo ao nome do ficheiro. No WhatsApp o painel e o
           cartao do documento e a legenda assenta na bolha, por baixo dele —
           era isso que o relato mostrava: o texto preso no rectangulo escuro.
           Aqui ela passa a ser irma do painel, como ja acontece na bolha de
           imagem (ver `.acl-image-bubble__caption` em Image.vue). -->
      <div
        v-if="caption"
        class="acl-file-bubble__caption"
        :class="isPrivateDocument
          ? 'acl-private-document-caption mx-1 mb-1 rounded-lg bg-[#fff8e7] px-3 pb-5 pt-2 text-[#111827] whitespace-pre-wrap break-words'
          : 'acl-media-caption-surface'"
      >
        {{ caption }}
      </div>

      <Teleport v-if="isPdfViewerOpen" to=".conversation-details-wrap">
        <PdfConversationViewer
          :url="url"
          :file-name="fileName"
          @close="closePdfViewer"
        />
      </Teleport>
    </BaseBubble>
  </div>
</template>

<style scoped>
/* Aceleres: painel do documento no tom cinza/branco do WhatsApp — sempre
   contrasta com o verde/branco da bolha, independente do sentido da
   mensagem. O preview do PDF fica colado por cima, sem gap. */
.acl-file-bubble__preview {
  overflow: hidden;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.acl-file-bubble__preview img {
  display: block;
  width: 100%;
  height: auto;
}

.acl-file-bubble__body {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px;
  background: rgb(246, 245, 243);
}

/* Aceleres: fundo do painel (titulo + infos do arquivo) diferenciado por
   remetente, estilo WhatsApp — translucido sobre a bolha verde enviada. */
.acl-file-bubble__body.acl-file-bubble__body--out {
  background: rgba(194, 189, 184, 0.15);
}

.dark .acl-file-bubble__body {
  background: rgba(255, 255, 255, 0.06);
}

.acl-file-bubble__file {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.acl-file-bubble__icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.acl-file-bubble__icon :deep(svg) {
  width: 40px;
  height: 40px;
}

.acl-file-bubble__details {
  min-width: 0;
  flex: 1;
}

.acl-file-bubble__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
}

.acl-file-bubble__info {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 1px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.55);
}

.dark .acl-file-bubble__info {
  color: rgba(255, 255, 255, 0.6);
}

.acl-file-bubble__separator {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.7;
}

.acl-file-bubble__caption {
  margin-top: 8px;
  font-size: 14px;
  line-height: 19px;
}
</style>

<style lang="css">
/* Aceleres 2026-09-02: a bolha de documento ganhou classe propria
   (`acl-document-bubble`) e medida fixa de 336x194, pedida pelo desenho.
   Este bloco NAO e `scoped`, e isso e a correcao de 04:30: a raiz da bolha e
   a raiz de OUTRO componente (BaseBubble) e o invólucro do conteudo tambem
   nasce la, entao os seletores com `[data-v-...]` do bloco `scoped` nao os
   alcancavam — a bolha continuava com a largura natural (420px medidos na
   tela). A raiz e escrita como `.acl-document-bubble.acl-file-bubble` — as
   duas classes vivem no mesmo elemento — para valer (0,2,0) contra os (0,1,0)
   das utilitarias do Tailwind: assim a medida ganha por especificidade, e nao
   pela ordem em que os blocos entram no bundle, que nao esta sob controle
   deste arquivo. Todo seletor comeca pela classe da bolha para nao vazar para
   o resto da conversa.
   O `min-w-[320px]` que existia no template saiu junto — com largura fixa ele
   so poderia contradize-la. */
.acl-document-bubble.acl-file-bubble {
  display: flex;
  flex-direction: column;
  /* Os quatro limites sao explicitos de proposito: o BaseBubble traz
     `min-w-[4.5rem]` e `max-w-[min(85vw,42rem)]` como utilitarias, e sem
     travar minimo e maximo a medida pedida (336x194) ficaria a merce delas em
     telas estreitas. */
  width: 336px;
  min-width: 336px;
  max-width: 336px;
  height: 194px;
  min-height: 194px;
  max-height: 194px;
  box-sizing: border-box;
}

/* Entre a bolha e o painel existe o invólucro do conteudo, que vem do
   BaseBubble (`.acl-file-bubble__content`). Sem transformá-lo tambem em
   coluna flexivel, a altura dele fica livre, o horario e empurrado para fora
   dos 194px e some. */
.acl-document-bubble .acl-file-bubble__content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

/* O painel do arquivo tem altura propria; quem absorve a sobra e o preview.
   Sem isto, o preview do PDF (imagem de altura livre) empurraria o painel
   para fora dos 194px. */
.acl-document-bubble .acl-file-bubble__preview {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.acl-document-bubble .acl-file-bubble__preview img {
  height: 100%;
  object-fit: cover;
  object-position: top center;
}

.acl-document-bubble .acl-file-bubble__body {
  flex: 0 0 auto;
}

/* Aceleres 2026-09-11: aqui o painel do ficheiro SEM preview esticava para
   ocupar a altura toda, com o conteudo centrado. Isso pressupunha a altura
   fixa de 194px, que era a medida do cartao do PDF (118 de imagem + faixa do
   nome) aplicada tambem a quem nao tem imagem nenhuma — dava uma linha de
   conteudo a flutuar no meio de ~90px de vazio, que foi o que o relato de
   11/09 mostrou ao lado da referencia do WhatsApp.

   O cartao sem preview passou a ter altura natural. Quem decide isso e o
   `public/aceleres-overrides.css` (bloco "DOCUMENTO SEM PRE-VISUALIZACAO", no
   fim do ficheiro), porque as medidas do cartao vivem la com `!important` e
   nenhuma regra deste componente ganha delas — ver o comentario do bloco de
   336x194 logo acima.

   A regra sai daqui em vez de ficar a contradizer a de la: enquanto as duas
   existirem com valores opostos, esta e codigo morto que engana quem vier
   ajustar o cartao — foi assim que o microfone verde sobreviveu meses no
   ficheiro do audio. */
</style>
